import {
  GroupPostAttributesSchedulingType,
  PostEditorPostError,
  PostEditorPostErrors,
  PostEditorRecurringIntervalType,
  PostGroupsType
} from '../../../models/post-editor';
import { ErrorMessageSeverity } from '../../../constants/form';
import { PostEditorValidationMessages } from '../constants/validation-messages';
import { severityResolver } from './helpers/severity-resolver';
import { PostTiming } from '../../../models/graphql';
import {
  addDays,
  addWeeks,
  addYears,
  differenceInDays,
  isBefore,
  parse,
  setHours,
  setMinutes,
  setSeconds
} from 'date-fns';
import { DateStringFormat, DateTimeStringFormat } from '../../../constants/time-formats';

const hasEnoughVariationsForRecurring = (
  postDate: Date,
  expiryDate: Date,
  intervalCount: number,
  intervalType: PostEditorRecurringIntervalType,
  numberOfVariations: number
): boolean => {
  let lastCycleDate: Date;
  const numberOfIntervals = intervalCount * (numberOfVariations - 1); // we remove 1, since first variation is the specific dateTime, and recurring starts after
  switch (intervalType) {
    case PostEditorRecurringIntervalType.years:
      lastCycleDate = addYears(postDate, numberOfIntervals);
      break;
    case PostEditorRecurringIntervalType.weeks:
      lastCycleDate = addWeeks(postDate, numberOfIntervals);
      break;
    default:
      lastCycleDate = addDays(postDate, numberOfIntervals);
  }
  return isBefore(lastCycleDate, expiryDate);
};
const isInvalid = (totalVariations: number, scheduling: GroupPostAttributesSchedulingType) => {
  if (totalVariations <= 1) {
    return false;
  }
  const qntyScheduledPostsDates =
    scheduling.postTiming === PostTiming.SCHEDULE ? scheduling.scheduledLocalDateTimes.length : 0;
  const referenceDate = new Date();
  const isQueue = scheduling.postTiming === PostTiming.QUEUE;
  const isRecurring = scheduling.postTiming === PostTiming.RECURRING && scheduling.recurring.active;
  const isEvergreen = scheduling.evergreen.active && !isRecurring;
  const hasEnoughDates = qntyScheduledPostsDates >= totalVariations;
  const evergreenExpireType = isEvergreen ? scheduling.evergreen.expireType : [];
  const recurringExpireType = isRecurring ? scheduling.recurring.expireType : [];
  const isPostNowAndEverGreen = isEvergreen && scheduling.postTiming === PostTiming.NOW;
  const isPostNowAndEverGreenNotCount = isPostNowAndEverGreen && !evergreenExpireType.includes('count');
  const evergreenExpiryingDate =
    isEvergreen && scheduling.evergreen.expiryDate && evergreenExpireType.includes('date')
      ? setSeconds(
          setMinutes(setHours(parse(scheduling.evergreen.expiryDate, DateStringFormat, referenceDate), 23), 59),
          59
        )
      : null; // we set the expiry value at the end of day
  const recurringExpiringDate =
    isRecurring && scheduling.recurring.expiryDate && recurringExpireType.includes('date')
      ? setSeconds(
          setMinutes(setHours(parse(scheduling.recurring.expiryDate, DateStringFormat, referenceDate), 23), 59),
          59
        )
      : null; // we set the expiry value at the end of day

  const isPostNowAndEverGreenMaxRepeats =
    isPostNowAndEverGreen &&
    evergreenExpireType.includes('count') &&
    !!scheduling.evergreen.expiryMaxRepeats &&
    scheduling.evergreen.expiryMaxRepeats >= totalVariations;

  const hasEnoughMaxRepeatsRecurring =
    isRecurring &&
    scheduling.recurring.expireType.includes('count') &&
    !!scheduling.recurring.expiryMaxRepeats &&
    scheduling.recurring.expiryMaxRepeats >= totalVariations;
  const hasEnoughDaysRecurring =
    isRecurring &&
    !!scheduling.recurring.date &&
    scheduling.recurringValue &&
    scheduling.recurringType &&
    recurringExpiringDate &&
    hasEnoughVariationsForRecurring(
      parse(scheduling.recurring.date, DateTimeStringFormat, referenceDate),
      recurringExpiringDate, // we set the expiry value at the end of day
      scheduling.recurringValue,
      scheduling.recurringType,
      totalVariations
    );
  const neverExpireRecurring = isRecurring && recurringExpireType.length === 0;
  const hasEnoughMaxRepeatsEvergreen =
    evergreenExpireType.includes('count') &&
    !!scheduling.evergreen.expiryMaxRepeats &&
    scheduling.evergreen.expiryMaxRepeats >= totalVariations;

  const hasEnoughDaysEvergreen =
    evergreenExpireType.includes('date') &&
    !!scheduling.evergreen.expiryDate &&
    evergreenExpiryingDate &&
    Math.abs(differenceInDays(evergreenExpiryingDate, referenceDate)) + qntyScheduledPostsDates >= totalVariations;

  const neverExpireEvergreen = isEvergreen && evergreenExpireType.length === 0;

  const hasEnoughDatesQueue =
    isQueue &&
    !!evergreenExpiryingDate &&
    evergreenExpireType.includes('date') &&
    Math.abs(differenceInDays(evergreenExpiryingDate, referenceDate)) + 1 >= totalVariations; // we add one assuming we queue it for today

  const neverExpireQueue = isQueue && isEvergreen && evergreenExpireType.length === 0;

  const hasEnoughMaxRepeatsQueue =
    isQueue &&
    isEvergreen &&
    evergreenExpireType.includes('count') &&
    !!scheduling.evergreen.expiryMaxRepeats &&
    scheduling.evergreen.expiryMaxRepeats >= totalVariations;

  const isValid =
    hasEnoughDates ||
    hasEnoughMaxRepeatsRecurring ||
    hasEnoughDaysRecurring ||
    neverExpireRecurring ||
    hasEnoughMaxRepeatsEvergreen ||
    hasEnoughDaysEvergreen ||
    neverExpireEvergreen ||
    isPostNowAndEverGreenMaxRepeats ||
    isPostNowAndEverGreenNotCount ||
    hasEnoughDatesQueue ||
    neverExpireQueue ||
    hasEnoughMaxRepeatsQueue;
  return !isValid;
};
export const scheduleTextOrMediaVariations = (
  postGroup: PostGroupsType,
  severity: ErrorMessageSeverity
): PostEditorPostErrors => {
  const { customizeSchedulePerProfile, customizeContentPerProfile } = postGroup;
  let results: PostEditorPostError[] = [];
  if (customizeSchedulePerProfile || customizeContentPerProfile) {
    postGroup.posts.forEach(post => {
      const totalVariations = post.content.variationMeta.total - post.content.variationMeta.prevPostedCount;
      if (isInvalid(totalVariations, post.scheduling)) {
        results = [
          ...results,
          {
            profileId: post.customizedProfileId,
            variationId: null,
            severity: severityResolver(post, severity),
            selectedSocialProfiles: [],
            message: PostEditorValidationMessages.schedule.scheduleTextOrMediaVariations
          }
        ];
      }
    });
    return results.length ? results : false;
  } else {
    const { scheduling, content } = postGroup.groupPostAttributes;
    const totalVariations = content.variationMeta.total;

    return isInvalid(totalVariations, scheduling)
      ? {
          profileId: null,
          variationId: null,
          severity: severityResolver(postGroup.groupPostAttributes, severity),
          selectedSocialProfiles: [],
          message: PostEditorValidationMessages.schedule.scheduleTextOrMediaVariations
        }
      : false;
  }
};
